import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Knopf } from "../ui/Knopf";
import { Feld } from "../ui/Feld";
import { Wortmarke } from "../ui/Wortmarke";

/**
 * Sign-in.
 *
 * Deliberately plain: a solid panel carrying the mark and one line of positioning,
 * and a form. No hero, no gradient, no illustration — the people who log in here do
 * it every weekday morning and the screen's only job is to get out of the way.
 */
export function Anmeldung() {
  const { anmeldenMitGoogle, anmeldenMitMicrosoft, anmeldenMitEmail } = useAuth();
  const [email, setEmail] = useState("");
  const [passwort, setPasswort] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<"google" | "microsoft" | "email" | null>(null);

  async function mitAnbieter(anbieter: "google" | "microsoft") {
    setFehler(null);
    setLaeuft(anbieter);
    try {
      await (anbieter === "google" ? anmeldenMitGoogle() : anmeldenMitMicrosoft());
      // The browser navigates away to the provider; nothing follows.
    } catch (error) {
      setFehler((error as Error).message);
      setLaeuft(null);
    }
  }

  async function mitEmail(event: FormEvent) {
    event.preventDefault();
    setFehler(null);
    setLaeuft("email");
    try {
      await anmeldenMitEmail(email, passwort);
    } catch (error) {
      setFehler((error as Error).message);
    } finally {
      setLaeuft(null);
    }
  }

  return (
    <div className="min-h-dvh grid lg:grid-cols-[minmax(0,420px)_1fr]">
      {/* Left panel — solid, not a gradient. Carries the mark and the positioning line. */}
      <aside
        className="hidden lg:flex flex-col justify-between p-[48px]"
        style={{ background: "var(--surface-chrome)" }}
      >
        <Wortmarke invers />

        <div className="max-w-[34ch]">
          <p
            className="text-[length:var(--text-lg)]"
            style={{ color: "var(--text-inverse)", lineHeight: "var(--leading-snug)" }}
          >
            Hausverwaltung, vollständig nachvollziehbar geführt.
          </p>
          <p
            className="mt-[12px] text-[length:var(--text-sm)]"
            style={{ color: "var(--text-on-chrome-2)", lineHeight: "var(--leading-text)" }}
          >
            Vorschreibung, Buchhaltung, Abrechnung und Schadensbearbeitung in einem System —
            mit einem Prüfpfad hinter jeder Zahl.
          </p>
        </div>

        <p className="text-[length:var(--text-2xs)]" style={{ color: "var(--text-on-chrome-3)" }}>
          Betrieben in der Europäischen Union
        </p>
      </aside>

      {/* Right — the form. */}
      <main className="flex items-center justify-center p-[24px]">
        <div className="w-full max-w-[340px]">
          <div className="lg:hidden mb-[32px]">
            <Wortmarke />
          </div>

          <h1
            className="text-[length:var(--text-lg)]"
            style={{ fontWeight: "var(--weight-strong)", color: "var(--text-primary)" }}
          >
            Anmelden
          </h1>
          <p className="mt-[4px] text-[length:var(--text-sm)]" style={{ color: "var(--text-muted)" }}>
            Melden Sie sich mit Ihrem Arbeitskonto an.
          </p>

          <div className="mt-[24px] flex flex-col gap-[8px]">
            <Knopf
              variante="standard"
              onClick={() => void mitAnbieter("google")}
              laeuft={laeuft === "google"}
              disabled={laeuft !== null}
              icon={<GoogleZeichen />}
              className="w-full justify-start"
            >
              Mit Google anmelden
            </Knopf>

            {/*
              Shown but inert: Microsoft sign-in needs an Entra app registration in a
              tenant this deployment does not yet have. Leaving the button visible and
              disabled states the intent honestly — hiding it would suggest the product
              does not support Microsoft at all, and a button that fails on click is
              worse than one that explains itself.
            */}
            <Knopf
              variante="standard"
              disabled
              aria-disabled="true"
              title="Microsoft-Anmeldung ist für diese Installation noch nicht eingerichtet."
              icon={<MicrosoftZeichen />}
              className="w-full justify-start"
            >
              Mit Microsoft anmelden
            </Knopf>
            <p className="text-[length:var(--text-2xs)]" style={{ color: "var(--text-muted)" }}>
              Microsoft-Anmeldung ist für diese Installation noch nicht eingerichtet.
            </p>
          </div>

          <div className="my-[20px] flex items-center gap-[8px]">
            <span className="h-px flex-1" style={{ background: "var(--line-hairline)" }} />
            <span className="text-[length:var(--text-2xs)]" style={{ color: "var(--text-muted)" }}>
              oder
            </span>
            <span className="h-px flex-1" style={{ background: "var(--line-hairline)" }} />
          </div>

          <form onSubmit={(e) => void mitEmail(e)} className="flex flex-col gap-[12px]">
            <Feld
              label="E-Mail-Adresse"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Feld
              label="Passwort"
              type="password"
              autoComplete="current-password"
              required
              value={passwort}
              onChange={(e) => setPasswort(e.target.value)}
            />

            {fehler ? (
              <p
                role="alert"
                className="text-[length:var(--text-xs)] border px-[8px] py-[6px] rounded-[var(--radius-2)]"
                style={{
                  color: "var(--status-bad-tx)",
                  background: "var(--status-bad-bg)",
                  borderColor: "var(--status-bad-bar)",
                }}
              >
                {fehler}
              </p>
            ) : null}

            <Knopf
              type="submit"
              variante="primaer"
              laeuft={laeuft === "email"}
              disabled={laeuft !== null}
              className="w-full"
            >
              Anmelden
            </Knopf>
          </form>

          {/*
            Der Weg herein für eine Hausverwaltung, die es hier noch nicht gibt.
            Ohne diesen Satz ist die Registrierung zwar gebaut, aber unauffindbar —
            und eine Anmeldemaske ohne Ausweg ist für einen neuen Kunden eine
            verschlossene Tür.
          */}
          <p
            className="mt-[16px] text-[length:var(--text-xs)]"
            style={{ color: "var(--text-muted)" }}
          >
            Noch keine Hausverwaltung eingerichtet?{" "}
            <Link to="/registrierung" style={{ color: "var(--text-link)" }}>
              Organisation anlegen
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}

/* Provider marks are drawn inline rather than fetched, because AS-24 forbids any
   external CDN and these are the only two brand glyphs in the application. */

function GoogleZeichen() {
  return (
    <svg width="14" height="14" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z" />
      <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.9 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z" />
    </svg>
  );
}

function MicrosoftZeichen() {
  return (
    <svg width="14" height="14" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#F25022" d="M0 0h8.5v8.5H0z" />
      <path fill="#7FBA00" d="M9.5 0H18v8.5H9.5z" />
      <path fill="#00A4EF" d="M0 9.5h8.5V18H0z" />
      <path fill="#FFB900" d="M9.5 9.5H18V18H9.5z" />
    </svg>
  );
}
